import { mapToKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { getSum } from '@witmetrics/utils/numbers';
import type { Dictionary } from '@witmetrics/utils';
import { generateColors } from '@/utils/reports';
import { DATA_LABEL_CHIP } from '@/charts/Chart/constants';
import { buildBarGradientFill } from '@/charts/Chart/utils';
import type { BarChartOptions, BarChartParams, ComparisonRole } from './types';
import {
  CHIP_FILL_TOLERANCE,
  CHIP_HEADROOM,
  CHIP_LIFT,
  CHIP_TICKS,
  COMPARISON_GRADIENT,
  NICE_STEPS,
} from './constants';

export const classes = {
  wrapper: 'h-full',
};

export const styles = {
  wrapper: {
    '& .apexcharts-bar-area': {
      cursor: 'pointer',
    },
  },
};

function getDataMax<T extends Dictionary>(
  { data, series }: BarChartParams<T>,
  stacked: boolean
) {
  const rowValues = data.map((d) =>
    series.map((s) => (d[s] as number | null) ?? 0)
  );
  const rowMax = rowValues.map((values) =>
    stacked ? getSum(...values) : Math.max(...values)
  );
  return Math.max(0, ...rowMax);
}

// Brand gradient for the current period, teal for the previous, in series order
export function buildComparisonBarStyle(order: ComparisonRole[]) {
  const gradients = order.map((role) => COMPARISON_GRADIENT[role]);
  return {
    colors: gradients.map((g) => g.bottom),
    fill: buildBarGradientFill(gradients),
  };
}

// Tightest round-stepped axis that still leaves headroom for the chips
export function buildChipBounds(dataMax: number) {
  if (dataMax <= 0) return null;
  const target = dataMax * CHIP_HEADROOM;
  const base = Math.pow(10, Math.floor(Math.log10(target)) - 1);
  const candidates = [base, base * 10, base * 100]
    .flatMap((magnitude) =>
      NICE_STEPS.map((n, rank) => ({ step: n * magnitude, rank }))
    )
    .map(({ step, rank }) => {
      const max = Math.ceil(target / step) * step;
      return { rank, max, tickAmount: Math.round(max / step) };
    })
    .filter(
      (c) => c.tickAmount >= CHIP_TICKS.min && c.tickAmount <= CHIP_TICKS.max
    );
  if (isEmpty(candidates)) return null;
  const tightest = Math.min(...candidates.map((c) => c.max));
  const [best] = candidates
    .filter((c) => c.max <= tightest * CHIP_FILL_TOLERANCE)
    .sort((a, b) => a.rank - b.rank || a.tickAmount - b.tickAmount);
  return { min: 0, max: best.max, tickAmount: best.tickAmount };
}

export function formatData<T extends Dictionary>(
  params: BarChartParams<T>,
  {
    orientation,
    stacked = false,
    dataLabels = true,
    hideZeroLabels = false,
    animations = true,
    legend = true,
    formatValue,
    colors,
    fill,
    columnWidth,
    barRadius,
    barGap,
    dataLabelsPosition,
    dataLabelColors,
    dataLabelChips,
  }: BarChartOptions
) {
  // A transparent stroke also insets the bar top and bottom; narrowing the column does not
  const formatSeriesData = (key: keyof T) => {
    if (!barGap) return params.data.map((d) => d[key] as number);
    return params.data.map((d) => ({
      x: d[params.xAxis],
      y: d[key],
      columnWidthOffset: -barGap,
    }));
  };
  const formatLabel = (value: number) => {
    if (hideZeroLabels && !value) return '';
    return formatValue ? formatValue(value) : `${value}`;
  };
  const chipBounds = dataLabelChips
    ? buildChipBounds(getDataMax(params, stacked))
    : null;
  return {
    series: params.series.map((s) => ({
      name: params.labels?.[s] || (s as string),
      data: formatSeriesData(s),
    })),
    options: {
      colors: colors ?? generateColors(params.series.length),
      ...(fill && { fill }),
      legend: { show: legend },
      xaxis: { categories: mapToKey(params.data, params.xAxis) },
      ...((formatValue || chipBounds) && {
        yaxis: {
          ...(formatValue && { labels: { formatter: formatValue } }),
          ...chipBounds,
        },
      }),
      dataLabels: {
        ...(dataLabelChips && { ...DATA_LABEL_CHIP, offsetY: -CHIP_LIFT }),
        enabled: dataLabels,
        ...((formatValue || hideZeroLabels) && { formatter: formatLabel }),
        ...(dataLabelColors && { style: { colors: dataLabelColors } }),
      },
      tooltip: {
        // Much nicer instead of having it jump left & right around the bars
        followCursor: orientation === 'horizontal',
        ...(orientation === 'vertical' && { shared: true, intersect: false }),
        ...(formatValue && { y: { formatter: formatValue } }),
      },
      plotOptions: {
        bar: {
          horizontal: orientation === 'horizontal',
          ...(columnWidth && { columnWidth }),
          ...(barRadius !== undefined && { borderRadius: barRadius }),
          ...(dataLabelsPosition && {
            dataLabels: { position: dataLabelsPosition },
          }),
        },
      },
      chart: {
        stacked,
        animations: { enabled: animations },
      },
    },
  };
}
